import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";
import StyledSpinner from "@/components/styled/StyledSpinner";
import { ControlledUnifiedInput } from "@/components/ui/customs/controlled-unified-input";
import { RibDto } from "@/types/api/api-types";
import { useUpdateRib } from "@/api/rib.api";
import { RibFormData, RibSchema } from "@/components/backOffice/parameters/options/rib/rib.form";
import { Button } from "@/components/ui/button";
import { ControlledCheckbox } from "@/components/ui/customs/controlled-checkbox";
import { Modal } from "@/components/ui/customs/modal";

type OptionsRibUpdateModalProps = {
  closeModal: () => void;
  rib: RibDto;
};

export const OptionsRibUpdateModal = ({ closeModal, rib }: OptionsRibUpdateModalProps) => {
  const { t } = useTranslation(["boParameters", "common"]);
  const { mutateAsync: updateRib, isPending } = useUpdateRib();

  const ribForm = useForm<RibFormData>({
    resolver: zodResolver(RibSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
    defaultValues: rib,
  });

  const onSubmit: SubmitHandler<RibFormData> = async (data) => {
    await updateRib({ id: rib.id, payload: data });
    closeModal();
  };

  return (
    <Modal
      open
      onClose={closeModal}
      title={t("ribUpdateModal.title")}
      contentClassName="max-w-[600px]"
      footer={
        <>
          <Button
            type="button"
            onClick={ribForm.handleSubmit(onSubmit)}
            disabled={isPending}
            className="w-full"
          >
            {isPending ? <StyledSpinner /> : t("ribUpdateModal.submit")}
          </Button>
          <Button variant="ghost" onClick={closeModal} disabled={isPending} className="w-full">
            {t("common:actions.cancel")}
          </Button>
        </>
      }
    >
      <form onSubmit={ribForm.handleSubmit(onSubmit)} className="flex w-full flex-col gap-4 p-6">
        <ControlledUnifiedInput
          control={ribForm.control}
          name="shortenName"
          label={t("ribUpdateModal.shortenNameLabel")}
          inputType="text"
        />
        <ControlledUnifiedInput
          control={ribForm.control}
          name="beneficiary"
          label={t("ribUpdateModal.beneficiaryLabel")}
          inputType="text"
        />
        <ControlledUnifiedInput
          control={ribForm.control}
          name="iban"
          label="IBAN"
          inputType="text"
        />
        <ControlledUnifiedInput control={ribForm.control} name="bic" label="BIC" inputType="text" />
        <ControlledUnifiedInput
          control={ribForm.control}
          name="bankName"
          label={t("ribUpdateModal.bankNameLabel")}
          inputType="text"
        />
        <ControlledUnifiedInput
          control={ribForm.control}
          name="bankAddress"
          label={t("ribUpdateModal.bankAddressLabel")}
          inputType="text"
        />
        <ControlledCheckbox
          control={ribForm.control}
          name="isDefault"
          label={t("ribUpdateModal.isDefaultLabel")}
        />
      </form>
    </Modal>
  );
};
